import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { useI18n } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Chatbot = () => {
  const { t, lang } = useI18n();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: "assistant", content: t.chatbot.welcome }]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [consent, setConsent] = useState(false);
  const sessionId = useRef(`sess-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading || !consent) return;
    setMessages((m) => [...m, { role: "user", content: text }]);
    setInput("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/chat`, {
        session_id: sessionId.current,
        message: text,
        lang,
      });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (err) {
      setMessages((m) => [
        ...m,
        { role: "assistant", content: t.chatbot.unavailable },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        data-testid="chatbot-toggle"
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-5 right-5 z-[90] w-14 h-14 rounded-full bg-brand hover:bg-brand-hover text-white shadow-xl shadow-brand/30 flex items-center justify-center btn-lift"
        aria-label="Chatbot"
      >
        {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chatbot-window"
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className="fixed bottom-24 right-5 z-[90] w-[calc(100vw-2.5rem)] sm:w-96 h-[520px] max-h-[75vh] bg-white rounded-lg shadow-2xl border border-navy/10 flex flex-col overflow-hidden"
          >
            <div className="bg-navy text-white p-4 flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-brand flex items-center justify-center">
                <MessageCircle className="w-5 h-5" />
              </div>
              <div>
                <p className="font-head font-semibold text-sm">{t.chatbot.title}</p>
                <p className="text-xs text-white/60">{t.chatbot.subtitle}</p>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-3 thin-scroll bg-[#f8fafc]">
              {messages.map((m, i) => (
                <div key={i} data-testid={`chat-msg-${i}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[80%] px-3.5 py-2.5 rounded-lg text-sm whitespace-pre-wrap leading-relaxed ${
                      m.role === "user"
                        ? "bg-navy text-white rounded-br-sm"
                        : "bg-white border border-navy/10 text-slate-700 rounded-bl-sm"
                    }`}
                  >
                    {m.content}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="bg-white border border-navy/10 px-3.5 py-2.5 rounded-lg flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-brand" />
                    <span className="text-sm text-slate-400">Píše…</span>
                  </div>
                </div>
              )}
              <div ref={bottomRef} />
            </div>

            <div className="border-t border-navy/10 bg-white">
              {!consent && (
                <label data-testid="chat-gdpr" className="flex items-start gap-2 px-3 pt-3 pb-1 cursor-pointer">
                  <Checkbox
                    data-testid="chat-gdpr-checkbox"
                    checked={consent}
                    onCheckedChange={(v) => setConsent(!!v)}
                    className="mt-0.5 data-[state=checked]:bg-brand data-[state=checked]:border-brand"
                  />
                  <span className="text-[11px] text-slate-500 leading-snug">
                    {t.chatbot.gdprPre}
                    <Link to="/ochrana-osobnych-udajov" className="text-brand underline" target="_blank">{t.chatbot.gdprLink}</Link>.
                  </span>
                </label>
              )}
              <div className="p-3 flex items-center gap-2">
                <input
                  data-testid="chat-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && send()}
                  disabled={!consent}
                  placeholder={consent ? t.chatbot.placeholder : t.chatbot.needConsent}
                  className="flex-1 px-3 py-2.5 rounded-md border border-navy/15 text-sm focus:outline-none focus:ring-2 focus:ring-brand/40 disabled:bg-slate-50 disabled:cursor-not-allowed"
                />
                <button
                  data-testid="chat-send"
                  aria-label="Odoslať správu"
                  onClick={send}
                  disabled={loading || !consent}
                  className="w-10 h-10 rounded-md bg-brand hover:bg-brand-hover text-white flex items-center justify-center disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
